import React from 'react'
import { dayjs } from '@mobi/utils/date'
import { toMoney } from '@mobi/utils/money'
import type { KambiValidReward } from '@mobi/api-types/kambi/player/reward'
import { colors, radius, spacing } from '@mobi/component-library/Foundations'
import { PromoSelector } from '@mobi/betslip/Components/Common/PromoSelector'
import { PromoSelectionSimple } from '@mobi/betslip/Components/Common/PromoSelector/Components/PromoSelectionSimple'
import type { PromoSelectionObject } from '@mobi/betslip/Components/Common/PromoSelector/types'
import { formatKambiCurrencyToDollarDecimal } from '@mobi/sports-offering/helpers/currency/currencyFormat'
import { getKambiRewardId } from '@core/Areas/Quickbet/Components/KambiReward/helpers/getRewardId'
import { SelectKambiReward } from '@core/Areas/Quickbet/signals'
import { getKambiRewardTitle } from './helpers/getRewardTitle'
import { getKambiRewardDescription } from './helpers/getRewardDescription'
import { autoSelectDefaultReward } from './helpers/autoSelectDefaultReward'

export const RewardSelection: React.FC<{
  rewards: KambiValidReward[]
  selectedReward: KambiValidReward | null
  setHasCheckedAutoSelectByDefault(): void
  hasCheckedAutoSelectByDefaultRef: React.RefObject<boolean>
}> = ({
  rewards,
  selectedReward,
  setHasCheckedAutoSelectByDefault,
  hasCheckedAutoSelectByDefaultRef,
}) => {
  React.useEffect(() => {
    if (hasCheckedAutoSelectByDefaultRef.current) return
    autoSelectDefaultReward(rewards)
    setHasCheckedAutoSelectByDefault()
  }, [rewards, hasCheckedAutoSelectByDefaultRef, setHasCheckedAutoSelectByDefault])

  const isSingleRewardActive = rewards.length === 1

  const promotionObj = rewardToPromoSelection(rewards, selectedReward)

  return (
    <div style={{ marginBottom: spacing.md }}>
      {isSingleRewardActive ? (
        <PromoSelectionSimple
          promoType='KambiReward'
          title={promotionObj[0].title}
          isSelected={promotionObj[0].isSelected}
          hasActiveSelection
          onClick={promotionObj[0].onClick as () => void}
          descriptionSlots={promotionObj[0].descriptionSlots}
          setSelectionHeight={() => null}
          isPromoSelectorExpanded
          renderPosition={1}
          isSingularDisplay
          style={{
            background: colors.lavender[50],
            border: `1px solid ${colors.lavender[300]}`,
            borderRadius: radius.lgx1,
            padding: `${spacing.sm} ${spacing.md}`,
          }}
        />
      ) : (
        <PromoSelector
          key='promo-selector-kambi-reward'
          promoType='KambiReward'
          itemId='promo-selector-kambi-reward'
          selections={promotionObj}
        />
      )}
    </div>
  )
}

// =============
// Local Helpers
// =============

function rewardToPromoSelection(
  validRewards: KambiValidReward[],
  selectedValidReward: KambiValidReward | null
): PromoSelectionObject[] {
  return validRewards.map(reward => {
    const rewardId = getKambiRewardId(reward)
    const isSelected = !!(selectedValidReward && getKambiRewardId(selectedValidReward) === rewardId)

    const rewardTitle = getKambiRewardTitle(reward)
    const description = getKambiRewardDescription(reward)
    const maxStake = getMaxStake(reward)

    return {
      title: rewardTitle,
      onClick: () => SelectKambiReward(reward),
      isSelected,
      selectionId: rewardId,
      descriptionSlots: [
        <div key='reward-description'>{description}</div>,
        maxStake && (
          <div key='reward-stake'>
            Max stake {toMoney(formatKambiCurrencyToDollarDecimal(maxStake))}
          </div>
        ),
        reward.expirationDate && (
          <div key='reward-expiry'>
            Expires {dayjs(reward.expirationDate).format('D MMM YYYY, h:mm a')}
          </div>
        ),
      ],
    }
  })
}

function getMaxStake(reward: KambiValidReward): number | undefined {
  switch (reward.rewardType) {
    case 'FREE_BET':
      return reward.freeBetProperties?.amount
    case 'PROFIT_BOOST':
      return reward.profitBoostProperties?.maxStake
    case 'ODDS_BOOST':
      return reward.oddsBoostProperties?.maxStake
    case 'SECOND_CHANCE':
      return reward.secondChanceProperties?.maxStake
    default:
      return undefined
  }
}
